import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Package2, ChevronDown, Calculator, DollarSign } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, createStockTransaction } from '../lib/stockItems';
import { ProductPhotoService } from '../services/productPhotoService';

interface ReceiveStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ReceiveStockModal({
  isOpen,
  onClose,
  onSuccess
}: ReceiveStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<StockItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
      
      // Load photos for all items
      await loadProductPhotos(data.map(item => item.id));
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load items');
    }
  };

  const loadProductPhotos = async (stockItemIds: string[]) => {
    if (stockItemIds.length === 0) return;

    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
      
      // Convert to the format expected by the component
      const photoUrls: Record<string, string> = {};
      Object.entries(photoMap).forEach(([itemId, photo]) => {
        photoUrls[itemId] = photo.photo_url;
      });
      
      setProductPhotos(photoUrls);
    } catch (err) {
      console.error('Error loading product photos:', err);
      // Don't show error to user, just continue without photos
    }
  };

  // Component for item icon - shows photo if available, otherwise blue icon
  const ItemIcon = ({ item, size = 'medium' }: { item: StockItem; size?: 'small' | 'medium' }) => {
    const photoUrl = productPhotos[item.id];
    const sizeClasses = size === 'small' ? 'h-10 w-10' : 'h-12 w-12';
    const iconSizeClasses = size === 'small' ? 'h-5 w-5' : 'h-6 w-6';
    
    if (photoUrl) {
      return (
        <div className={`${sizeClasses} rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center`}>
          <img 
            src={photoUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, hide it and show the blue icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="w-full h-full rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg class="${iconSizeClasses} text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }
    
    // Default blue icon if no photo
    return (
      <div className={`${sizeClasses} rounded-lg bg-blue-100 flex items-center justify-center`}>
        <Package2 className={`${iconSizeClasses} text-blue-600`} />
      </div>
    );
  };

  // Filter items based on search query
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return [];
    const searchLower = searchQuery.toLowerCase();
    return items.filter(item => 
      !item.is_deleted &&
      (item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.sku?.toLowerCase().includes(searchLower))
    );
  }, [items, searchQuery]);

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    // First filter out deleted items
    const activeItems = items.filter(item => !item.is_deleted);
    
    const grouped = activeItems.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Filter categories based on search query
  const filteredCategories = itemsByCategory.filter(category => {
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItem(null);
      setQuantity('');
      setPrice('');
      setNotes('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    const receiveQuantity = parseInt(quantity);
    if (isNaN(receiveQuantity) || receiveQuantity <= 0) {
      setError('Please enter a valid quantity');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createStockTransaction({
          item_id: selectedItem.id,
          type: 'purchase',
          quantity: receiveQuantity,
          price: parseFloat(price) || selectedItem.acquisition_cost,
          notes: notes || null
      });

      onSuccess();
    } catch (err) {
      console.error('Error receiving stock:', err);
      setError(err instanceof Error ? err.message : 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[9999]`}
      onClick={onClose}
      style={{
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[700px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gray-900 border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Receive Stock
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content Section */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto bg-gray-50" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Item Selection Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <label className="block text-sm font-medium text-gray-900 mb-4">
                  Select Item to Receive
                </label>
                
                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Show search results directly if searching */}
                {searchQuery ? (
                  <div className="space-y-2">
                    {filteredItems.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="relative mx-auto w-16 h-16 mb-4">
                          <div className="absolute inset-0 bg-gray-100 rounded-full"></div>
                          <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                            <Package2 className="h-6 w-6 text-gray-400" />
                          </div>
                        </div>
                        <p className="text-gray-600">No items match your search</p>
                      </div>
                    ) : (
                      filteredItems.map((item) => (
                        <div
                          key={item.id}
                          className={`bg-white border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedItem?.id === item.id ? 'ring-2 ring-blue-500 bg-blue-50' : 'border-gray-200'
                          } ${
                            item.quantity <= item.reorder_point ? 'bg-red-50 border-red-200' : ''
                          }`}
                          onClick={() => {
                            setSelectedItem(item);
                            setPrice(item.acquisition_cost.toString());
                          }}
                        >
                          <div className="flex items-center">
                            <div className="mr-4">
                              <ItemIcon item={item} size="medium" />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 flex items-center space-x-2">
                                <span>{item.name}</span>
                                <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-lg">
                                  {item.category?.name || 'Uncategorized'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1 flex items-center space-x-2">
                                <span>Current: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></span>
                                {item.quantity <= item.reorder_point && (
                                  <span className="text-red-600 font-medium bg-red-100 px-2 py-1 rounded-lg">
                                    Below reorder point
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCategories.map((category) => (
                      <div key={category.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className={`w-full px-4 py-3 flex items-center justify-between hover:bg-white/30 transition-all duration-200 ${
                            category.items.some(item => item.quantity <= item.reorder_point) 
                              ? 'bg-red-50/30' 
                              : 'bg-green-50/30'
                          }`}
                        >
                          <div className="flex items-center">
                            <ChevronDown 
                              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                                expandedCategories.has(category.id) ? 'rotate-180' : ''
                              }`}
                            />
                            <span className={`ml-3 text-sm font-medium ${
                              category.items.some(item => item.quantity <= item.reorder_point)
                                ? 'text-red-800'
                                : 'text-green-800'
                            }`}>
                              {category.name}
                            </span>
                            {category.items.some(item => item.quantity <= item.reorder_point) && (
                              <span className="ml-2 text-xs text-red-600 font-medium bg-red-100 px-2 py-1 rounded-lg">
                                Low Stock
                              </span>
                            )}
                          </div>
                        </button>

                        {expandedCategories.has(category.id) && (
                          <div className="bg-gray-50 border-t border-gray-200 divide-y divide-gray-200">
                            {category.items.map((item) => (
                              <div
                                key={item.id}
                                className={`p-4 cursor-pointer hover:bg-gray-100 transition-colors ${
                                  selectedItem?.id === item.id ? 'bg-blue-50' : ''
                                } ${
                                  item.quantity <= item.reorder_point ? 'bg-red-50' : ''
                                }`}
                                onClick={() => {
                                  setSelectedItem(item);
                                  setPrice(item.acquisition_cost.toString());
                                }}
                              >
                                <div className="flex items-center">
                                  <div className="mr-3">
                                    <ItemIcon item={item} size="small" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                    <div className="text-xs text-gray-600 mt-0.5 flex items-center space-x-2">
                                      <span>Current: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></span>
                                      {item.quantity <= item.reorder_point && (
                                        <span className="text-red-600 font-medium">
                                          (Below reorder point)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedItem && (
                <>
                  {/* Quantity and Price Section */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Receiving Details</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Quantity */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity to Receive
                        </label>
                        <input
                          type="number"
                          required
                          min="1"
                          value={quantity}
                          onChange={(e) => setQuantity(e.target.value)}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                          placeholder={`Quantity in ${selectedItem.unit}`}
                        />
                      </div>

                      {/* Price */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Unit Cost
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <DollarSign className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="number"
                            required
                            step="0.01"
                            min="0"
                            value={price}
                            onChange={(e) => setPrice(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notes Section */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <label className="block text-sm font-medium text-gray-900 mb-4">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none text-sm"
                      placeholder="Add any notes about this stock receipt..."
                    />
                  </div>

                  {/* Summary Section */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                      <Calculator className="h-4 w-4 mr-2 text-blue-600" />
                      Receiving Summary
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Item:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedItem.name}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Current Stock:</span>
                        <span className="text-sm font-semibold text-blue-700">{selectedItem.quantity} {selectedItem.unit}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Receiving:</span>
                        <span className="text-sm font-semibold text-green-600">+{quantity || 0} {selectedItem.unit}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <span className="text-sm font-semibold text-gray-700">New Stock Total:</span>
                        <span className="text-lg font-semibold text-green-600">
                          {selectedItem.quantity + (parseInt(quantity) || 0)} {selectedItem.unit}
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <span className="text-sm font-semibold text-gray-700">Total Cost:</span>
                        <span className="text-lg font-semibold text-blue-600">
                          ${((parseFloat(price) || 0) * (parseInt(quantity) || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

            </div>
            
            {/* Submit Button */}
            <div className="bg-white border-t border-gray-200 px-6 py-4">
              <button
                type="submit"
                disabled={loading || !selectedItem || !quantity || !price}
                className={`w-full px-6 py-3 text-base font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  loading ? 'bg-gray-600' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Receiving...
                  </div>
                ) : (
                  'Receive Stock'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}